import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetCopyService } from '~/domain/services/structureSetService/structureSetCopyService';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useWarpStructureSet } from '~/hooks/structure-set/useWarpStructureSet';
import { useSegmentationMetrics } from '~/hooks/workflows/backend/tasks/useSegmentationMetrics';
import {
    QaMeasurement,
    replanningQaMeasurementState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { qaCoverage } from './qaCoverage';
import { useRegistrationQaRun } from './useRegistrationQaRun';
import { useRunDeformation } from './useRunDeformation';
import { useRunScans } from './useRunScans';
import { logErrorName } from '~/utils/logErrorName';

interface SubmittedMeasurement {
    readonly key: string;
    readonly target: readonly string[];
    readonly planning: readonly string[];
}

class QaPreparationError extends Error {
    constructor(readonly messageId: string) {
        super(messageId);
        this.name = 'QaPreparationError';
    }
}

// Every structure the model produced, bar the body outline: QA checks the registration, not the ROI list's picks.
function measurableRoiNumbers(set: StructureSet): Set<number> {
    return new Set(
        set.roiArray
            .filter(
                (roi) =>
                    (roi.interpretedType ?? '').toUpperCase() !==
                    RoiInterpretedType.EXTERNAL
            )
            .map((roi) => roi.roiNumber)
    );
}

const namesIn = (set: StructureSet, numbers: ReadonlySet<number>) =>
    set.roiArray
        .filter((roi) => numbers.has(roi.roiNumber))
        .map((roi) => roi.name);

// Deforms the planning scan's Contour+ structures with this run's field and has Verify measure them on the target.
export function useRegistrationQaMeasurement(): void {
    const patient = useSelectedPatient();
    const { target: targetImage } = useRunScans();
    const {
        phase,
        sourceSet,
        targetSet,
        blockedReason,
        attempted,
        contoursReady,
        calculate,
    } = useRegistrationQaRun();

    // Measure as soon as both Contour+ sets and the field are ready, before the QA step is opened.
    useEffect(() => {
        if (!blockedReason && !attempted && contoursReady) {
            calculate();
        }
    }, [blockedReason, attempted, contoursReady, calculate]);
    const deformation = useRunDeformation();
    const { warp } = useWarpStructureSet();
    const [measurement, setMeasurement] = useRecoilState(
        replanningQaMeasurementState
    );
    const submitted = useRef<SubmittedMeasurement | undefined>();

    const settle = (
        next: (run: SubmittedMeasurement) => QaMeasurement
    ): void => {
        const run = submitted.current;
        if (run) {
            setMeasurement((current) =>
                current?.key === run.key ? next(run) : current
            );
        }
    };

    const { submit } = useSegmentationMetrics({
        scope: 'registration-qa',
        onResult: (rows) =>
            settle((run) => {
                // A failed structure comes back unnamed: it is reported as not measured.
                const measured = rows.filter((row) => row.gt_roi_name !== null);
                return {
                    status: 'ready',
                    key: run.key,
                    rows: measured,
                    coverage: qaCoverage(
                        run.target,
                        run.planning,
                        measured.map((row) => row.gt_roi_name as string)
                    ),
                };
            }),
        onError: (detail) =>
            settle((run) => ({ status: 'failed', key: run.key, detail })),
    });
    // The effect submits with the latest hook state, not the render that started it.
    const submitRef = useRef(submit);
    useLayoutEffect(() => {
        submitRef.current = submit;
    });

    const key =
        phase === 'ready' && sourceSet && targetSet && deformation
            ? `${sourceSet.dicomUid}|${targetSet.dicomUid}|${deformation.registration.sopInstanceUid}`
            : undefined;
    const measuredKey = measurement?.key;

    useEffect(() => {
        if (
            !key ||
            measuredKey === key ||
            !sourceSet ||
            !targetSet ||
            !deformation ||
            !targetImage
        ) {
            return;
        }
        setMeasurement({ status: 'measuring', key });
        submitted.current = undefined;
        void (async () => {
            try {
                const { registration, transform } = deformation;
                // The field pulls each target point back into the planning scan: only that pairing is measured.
                if (
                    registration.sourceFrameOfReferenceUid !==
                        targetImage.frameOfReferenceUid ||
                    registration.targetFrameOfReferenceUid !==
                        sourceSet.image?.frameOfReferenceUid
                ) {
                    throw new QaPreparationError(
                        'replanning.qa.deformationMismatch'
                    );
                }
                const planningRois = measurableRoiNumbers(sourceSet);
                const targetRois = measurableRoiNumbers(targetSet);
                const deformed = await warp({
                    sourceStructureSet: sourceSet,
                    targetImage,
                    transform,
                    existingStructureSets: patient?.structureSets ?? [],
                    selectedRoiNumbers: planningRois,
                });
                const reference =
                    new StructureSetCopyService().copySameGeometry({
                        sourceStructureSet: targetSet,
                        targetImage,
                        existingStructureSets: [],
                        selectedRoiNumbers: targetRois,
                    }).structureSet;
                // Both on the target grid, or the numbers would compare two different scans.
                if (
                    deformed.image?.seriesUid !== targetImage.seriesUid ||
                    reference.image?.seriesUid !== targetImage.seriesUid
                ) {
                    throw new QaPreparationError(
                        'replanning.qa.deformationMismatch'
                    );
                }
                submitted.current = {
                    key,
                    target: namesIn(targetSet, targetRois),
                    planning: namesIn(sourceSet, planningRois),
                };
                await submitRef.current(deformed, reference, targetImage);
            } catch (error) {
                logErrorName('Registration QA could not be measured:', error);
                setMeasurement((current) =>
                    current?.key === key
                        ? {
                              status: 'failed',
                              key,
                              messageId:
                                  error instanceof QaPreparationError
                                      ? error.messageId
                                      : 'replanning.qa.prepareFailed',
                          }
                        : current
                );
            }
        })();
    }, [
        key,
        measuredKey,
        sourceSet,
        targetSet,
        deformation,
        targetImage,
        patient,
        warp,
        setMeasurement,
    ]);
}
